import { useCallback, useMemo } from 'react';
import { debounce } from 'throttle-debounce';

import { cx } from '@libs/classnames';
import { CapsuleButton } from '@ui/components/CapsuleButton';
import { Loader } from '@ui/components/Loader';
import { SearchInput } from '@ui/components/SearchInput';
import { Tabs } from '@ui/components/Tabs';
import { ThemeButton } from '@ui/components/ThemeButton';

import { MemberAux } from '@features/club/typings';
import { MemberDto, ParticipantDto, RcfAttributes } from '@features/shared/api/typings';

import { List } from './components/List/List';

import styles from './ClubParticipantsForm.module.css';

export type ClubParticipantFormStatus = 'JoinRequest' | 'Active' | 'Deleted' | 'Invited';

const tabs = [
  { value: 'Active', label: 'Активные' },
  { value: 'Invited', label: 'Приглашенные' },
  { value: 'JoinRequest', label: 'Заявки' },
  { value: 'Deleted', label: 'Удаленные' },
];

export type Props = {
  className?: string;
  onDelete: (userId: string) => void;
  onAccept: (userId: string) => void;
  onReject: (userId: string) => void;
  participants: MemberDto[];
  status: ClubParticipantFormStatus;
  setStatus: (status: ClubParticipantFormStatus) => void;
  isLoading?: boolean;
  currentUserId?: string;
  groupTitle?: string;
  onGroupTitleChange?: (newGroupTitle: string | null) => void;
  onMemberGroupNameChange?: (userId: string, groupName: string | null) => void;
  memberGroupNameOptions?: string[];
  onLoadMore?: () => void;
  onSearch: (text: string) => void;
  isRcf: boolean;
  onRcfAttributesChange?: (newValue: RcfAttributes) => void;
  // требование к камерам на видеоконтроле: колонка есть, когда есть обработчик
  onCamerasChange?: (userId: string, aux: MemberAux | null) => void;
};

export function ClubParticipantsForm({
  className,
  participants,
  onDelete,
  onAccept,
  onReject,
  status,
  setStatus,
  isLoading,
  currentUserId,
  groupTitle,
  onGroupTitleChange,
  onMemberGroupNameChange,
  memberGroupNameOptions,
  onLoadMore,
  onSearch,
  isRcf,
  onRcfAttributesChange,
  onCamerasChange,
}: Props) {
  const renderActions = useCallback(
    (participant: MemberDto | ParticipantDto) => {
      if (status === 'Active') {
        return (
          <ThemeButton compact onClick={() => onDelete(participant.userId)}>
            Удалить
          </ThemeButton>
        );
      } else if (status === 'JoinRequest') {
        return (
          <div className={styles.buttons}>
            <ThemeButton compact onClick={() => onAccept(participant.userId)}>
              Принять
            </ThemeButton>
            <ThemeButton compact onClick={() => onReject(participant.userId)}>
              Отклонить
            </ThemeButton>
          </div>
        );
      } else if (status === 'Deleted') {
        return (
          <ThemeButton compact onClick={() => onAccept(participant.userId)}>
            Восстановить
          </ThemeButton>
        );
      }
    },
    [status, onDelete, onAccept, onReject],
  );

  const handleSearch = useMemo(() => debounce(300, onSearch, { atBegin: false }), [onSearch]);

  return (
    <div className={cx(className, styles.root)}>
      <Tabs
        tabs={tabs}
        value={status}
        onChange={(status: string | number | boolean) =>
          setStatus(status as ClubParticipantFormStatus)
        }
      />

      <SearchInput
        className={styles.search}
        onQueryChange={handleSearch}
        placeholder="Поиск участников клуба"
      />

      {(isLoading && <Loader className={styles.loader} centered />) || (
        <List
          participants={participants}
          groupTitle={groupTitle}
          onGroupTitleChange={onGroupTitleChange}
          renderParticipantActions={renderActions}
          highlightedUserId={currentUserId}
          onMemberGroupNameChange={onMemberGroupNameChange}
          memberGroupNameOptions={memberGroupNameOptions}
          isRcf={isRcf}
          onRcfAttributesChange={onRcfAttributesChange}
          onCamerasChange={onCamerasChange}
        />
      )}

      {onLoadMore && (
        <div className={styles.loadMoreButtonContainer}>
          <CapsuleButton preset="gray" size="16m-40" onClick={onLoadMore}>
            Показать еще
          </CapsuleButton>
        </div>
      )}
    </div>
  );
}
